import * as React from 'react'

import { PlusIcon, Trash2Icon } from 'lucide-react'
import { makeObservable, observable, runInAction } from 'mobx'
import { Observer, observer } from 'mobx-react'

import {
  IInstructions,
  IPermitTypeField,
  IWorkflowStep,
  PermitFieldType,
} from '~/client/graph'
import ResourceFieldConfigurator from '~/client/src/desktop/components/ResourceFieldConfigurator'
import { Button } from '~/client/src/shared/components/Button/Button'
import { AddIcon } from '~/client/src/shared/components/icons/AddIcon'
import {
  conditionalPermitFields,
  equipmentPermitFieldTypes,
  locationOnlyPermitFieldTypes,
  materialPermitFieldTypes,
  materialResourcePermitFieldTypes,
  restrictedMaterialResourceAssociatedFieldTypes,
} from '~/client/src/shared/constants/PermitFieldTypeConstants'
import { FORM_MATERIAL_RESOURCE_ASSOCIATED_FIELDS_KEY } from '~/client/src/shared/constants/permitTypeFieldsConstants'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import FieldConfigurator from '../../FieldConfigurator/FieldConfigurator'
import FormFieldsModal from '../../FormFieldsModal'
import Instructions from '../../Instructions/Instructions'
import FormConfiguratorStore from '../FormConfigurator.store'
import DependentFieldsConfigurator from './DependentFieldsConfigurator/DependentFieldsConfigurator'
import { FIELD_ROW_CONTROL_REVEAL } from './rowControlReveal'
import SectionFieldList from './SectionFieldList'
import TableFieldsConfigurator from './TableFieldsConfigurator'

// The add/delete controls centre on the first line each field type draws: its title row,
// section header, list-type row or text box. Divider rows are one line, so they centre on the row.
const INSTRUCTIONS_CONTROL_SLOT = 'tw:mt-3 tw:h-[30px]'
const FIELD_CONTROL_SLOT = 'tw:mt-1 tw:h-[34px]'
const CONTROL_SLOT_BY_TYPE: Partial<Record<PermitFieldType, string>> = {
  [PermitFieldType.Section]: 'tw:mt-3 tw:h-9',
  [PermitFieldType.Subsection]: 'tw:mt-3 tw:h-8',
  [PermitFieldType.Checklist]: INSTRUCTIONS_CONTROL_SLOT,
  [PermitFieldType.Question]: INSTRUCTIONS_CONTROL_SLOT,
  [PermitFieldType.Text]: 'tw:h-11',
  [PermitFieldType.Divider]: 'tw:self-center',
}

interface IProps {
  depth: number
  field: IPermitTypeField
  isSectionExpanded?: boolean

  store: FormConfiguratorStore
  allWorkflowSteps?: IWorkflowStep[]
  onSectionToggle?(): void
  onNotificationFieldStepsChange?: (fieldId: string, stepIds: string[]) => void
}

@observer
export default class FormFieldConfigurator extends React.Component<IProps> {
  @observable private store: FormConfiguratorStore = null
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)

    this.store = props.store
    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  private get isFieldConfigCollapsed(): boolean {
    return !this.store.expandedConfigFieldIds.has(this.props.field.id)
  }

  private toggleFieldConfigCollapsed = () => {
    this.store.toggleFieldConfigExpanded(this.props.field.id)
  }

  public render() {
    return (
      <div className="col relative">
        <div className="forms-field-row tw:group/field-row tw:flex tw:items-start tw:gap-1">
          {this.addNewFieldBtn}
          <div className="col field-configuration tw:min-w-0 tw:flex-1 relative overflow-hidden">
            {this.fieldConfigurator}
          </div>
          {this.deleteIcon}
        </div>
      </div>
    )
  }

  private renderDependentFieldsConfigurator = (): JSX.Element => {
    const { field, store } = this.localProps
    const useResourceRendering =
      equipmentPermitFieldTypes.includes(field.type) ||
      materialResourcePermitFieldTypes.includes(field.type) ||
      locationOnlyPermitFieldTypes.includes(field.type)

    return (
      <DependentFieldsConfigurator
        field={field}
        allConditionalFields={store.conditionalFields}
        addNewConditionalField={store.addNewConditionalField}
        updateConditionalField={store.updateConditionalField}
        removeConditionalField={store.removeConditionalField}
        tableConfiguratorRenderer={this.renderTableConfigurator}
        renderNewRuleButton={
          useResourceRendering
            ? onClick => (
                <div>
                  <Button
                    variant="ghost"
                    onClick={onClick}
                    className="tw:w-fit tw:text-muted-foreground"
                    size="lg"
                  >
                    <AddIcon data-icon="inline-start" size="xs" />
                    {Localization.translator.addConditionalField}
                  </Button>
                </div>
              )
            : undefined
        }
        renderHeader={
          useResourceRendering
            ? (startCreating, { rulesCount, isExpanded, onToggle }) => (
                <ResourceFieldConfigurator.CollapsibleSectionHeader
                  count={rulesCount}
                  isExpanded={isExpanded}
                  onToggle={onToggle}
                  onCreateNew={startCreating}
                  countLabel={n =>
                    Localization.translator.xConditionalFields(n)
                  }
                  sectionLabel={Localization.translator.conditionalFields}
                  addButtonLabel={Localization.translator.addConditionalField}
                />
              )
            : undefined
        }
      />
    )
  }

  private get fieldConfigurator(): JSX.Element {
    const { field, store } = this.props
    const {
      updateField,
      availableFilterFieldsOptions,
      filterFieldsMap,
      isRecurringInspection,
    } = store

    if (field.instructions) {
      return (
        <Instructions
          className="mb12"
          instructions={field.instructions}
          onChange={this.changePermitTypeInstruction}
        />
      )
    }

    return (
      <FieldConfigurator
        field={field}
        onChange={updateField}
        isCollapsed={this.isFieldConfigCollapsed}
        onCollapseToggle={this.toggleFieldConfigCollapsed}
        isSectionExpanded={this.props.isSectionExpanded}
        onSectionToggle={this.props.onSectionToggle}
        availableFilterFieldsOptions={availableFilterFieldsOptions}
        filterFieldsMap={filterFieldsMap}
        isDynamicFilterShown={!isRecurringInspection}
        tableConfiguratorRenderer={this.renderTableConfigurator}
        materialSubFieldsRenderer={this.renderAssociatedFieldsSection}
        dependentFieldsRenderer={this.renderDependentFieldsTab}
        allWorkflowSteps={this.props.allWorkflowSteps}
        onNotificationFieldStepsChange={
          this.props.onNotificationFieldStepsChange
        }
      />
    )
  }

  private renderAssociatedFieldsSection = (
    field: IPermitTypeField,
  ): JSX.Element => {
    if (!materialResourcePermitFieldTypes.includes(field.type)) {
      return null
    }
    return <Observer>{() => this.associatedFieldsSection}</Observer>
  }

  private renderDependentFieldsTab = (): JSX.Element => {
    if (!this.areConditionalFieldsShown) {
      return null
    }
    return <Observer>{() => this.renderDependentFieldsConfigurator()}</Observer>
  }

  private renderTableConfigurator = (field: IPermitTypeField): JSX.Element => {
    if (field.type !== PermitFieldType.Table) {
      return null
    }

    const {
      conditionalFields,
      addNewConditionalField,
      updateConditionalField,
      removeConditionalField,
      reorderConditionalField,
    } = this.localProps.store

    return (
      <TableFieldsConfigurator
        field={field}
        allConditionalFields={conditionalFields}
        addNewTableField={addNewConditionalField}
        updateTableField={updateConditionalField}
        removeTableField={removeConditionalField}
        reorderTableField={reorderConditionalField}
      />
    )
  }

  private get controlSlotClassName(): string {
    const { field } = this.props
    const slot = field.instructions
      ? INSTRUCTIONS_CONTROL_SLOT
      : (CONTROL_SLOT_BY_TYPE[field.type] ?? FIELD_CONTROL_SLOT)

    return `tw:flex tw:w-7 tw:flex-none tw:items-center ${slot}`
  }

  private get addNewFieldBtn(): JSX.Element {
    const { depth, store } = this.props
    const { getRestrictedFieldsByDepth, singleUseFields, isAddBtnHidden } =
      store

    if (isAddBtnHidden) {
      return <div className="tw:w-7 tw:flex-none" />
    }

    return (
      <div className={this.controlSlotClassName}>
        <FormFieldsModal
          addNewField={this.addNewField}
          restrictedFields={getRestrictedFieldsByDepth(depth)}
          disabledFields={singleUseFields}
        >
          <Button
            variant="ghost"
            size="icon-sm"
            className={FIELD_ROW_CONTROL_REVEAL}
            aria-label={Localization.translator.addField}
          >
            <PlusIcon />
          </Button>
        </FormFieldsModal>
      </div>
    )
  }

  private get deleteIcon(): JSX.Element {
    const { field, store } = this.props

    if (!store.canRemoveField(field)) {
      return <div className="tw:w-7 tw:flex-none" />
    }

    return (
      <div className={this.controlSlotClassName}>
        <Button
          variant="destructive-ghost"
          size="icon-sm"
          aria-label={Localization.translator.delete}
          className={FIELD_ROW_CONTROL_REVEAL}
          onMouseDown={e => e.preventDefault()}
          onClick={this.showRemoveDialog}
        >
          <Trash2Icon />
        </Button>
      </div>
    )
  }

  private get areConditionalFieldsShown(): boolean {
    const { isMultiple, type } = this.localProps.field
    return (
      (!isMultiple || materialPermitFieldTypes.includes(type)) &&
      conditionalPermitFields.includes(type)
    )
  }

  private get associatedFieldsSection(): JSX.Element {
    const { field, store } = this.localProps

    if (!materialResourcePermitFieldTypes.includes(field.type)) {
      return null
    }

    const associatedRule = store.conditionalFields?.find(
      cf =>
        cf.fieldId === field.id &&
        cf.key === FORM_MATERIAL_RESOURCE_ASSOCIATED_FIELDS_KEY,
    )
    const fields = associatedRule?.values || []

    return (
      <SectionFieldList
        title={Localization.translator.xAssociatedFields(fields.length)}
        emptyLabel={Localization.translator.associatedFields}
        addButtonLabel={Localization.translator.addAssociatedField}
        fields={fields}
        restrictedFieldTypes={restrictedMaterialResourceAssociatedFieldTypes}
        tableConfiguratorRenderer={this.renderTableConfigurator}
        onAddField={(index, fieldType) =>
          store.addNewConditionalField(
            field.id,
            FORM_MATERIAL_RESOURCE_ASSOCIATED_FIELDS_KEY,
            index,
            fieldType,
          )
        }
        onUpdateField={(index, updated) =>
          store.updateConditionalField(associatedRule.values, index, updated)
        }
        onRemoveField={removed =>
          store.removeConditionalField(
            field.id,
            FORM_MATERIAL_RESOURCE_ASSOCIATED_FIELDS_KEY,
            removed.id,
          )
        }
        onReorderField={(fromIndex, toIndex) =>
          store.reorderConditionalField(
            associatedRule.values,
            fromIndex,
            toIndex,
          )
        }
      />
    )
  }

  private changePermitTypeInstruction = (instructions: IInstructions) => {
    const { field, store } = this.props
    store.changePermitTypeInstruction(field, instructions)
  }

  private addNewField = (fieldType: PermitFieldType) => {
    const { field, store } = this.props
    store.addNewField(fieldType, field)
  }

  private showRemoveDialog = () => {
    const { field, store } = this.props
    store.showRemoveDialog(field.id)
  }
}
